import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';

import { useStyles } from '../styles';
import { useAgentProfileDeleteControllerAgentProfileDeleteForUsersMutation } from '@shared/services/apiService';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import { showProfileConflictToast } from '@shared/utils/agentProfileConflictMessage';

interface DeleteAgentProfileModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  profile: any;
}

export const DeleteAgentProfileModal: React.FC<DeleteAgentProfileModalProps> = ({
  open,
  onClose,
  onSuccess,
  profile,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { showError } = useErrorHandler();
  const [isDeleting, setIsDeleting] = useState(false);

  const [deleteAgentProfile] = useAgentProfileDeleteControllerAgentProfileDeleteForUsersMutation();

  const handleDelete = async () => {
    if (!profile?.id) return;

    try {
      setIsDeleting(true);
      await deleteAgentProfile({
        profileId: profile.id,
      }).unwrap();

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('assistantsPage.success.title'),
          description:
            t('assistantsPage.success.deleted', { name: profile.name }) ||
            `Agent profile "${profile.name}" deleted successfully`,
        },
      });

      onSuccess();
    } catch (error) {
      if (
        showProfileConflictToast(
          error,
          t,
          t('assistantsPage.errors.deleteTitle'),
          t('assistantsPage.errors.deleteSuggestion'),
        )
      ) {
        return;
      }
      showError({
        title: t('assistantsPage.errors.deleteTitle'),
        error,
        suggestion: t('assistantsPage.errors.deleteSuggestion'),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (!profile) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.modalPaper }}>
      <DialogTitle>{t('assistantsPage.modals.deleteModal.title')}</DialogTitle>

      <DialogContent dividers className={classes.modalContent}>
        <Typography variant="body1" gutterBottom>
          {t('assistantsPage.modals.deleteModal.confirmation', { name: profile.name })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('assistantsPage.modals.deleteModal.warning')}
        </Typography>
      </DialogContent>

      <DialogActions className={classes.modalActions}>
        <Button onClick={onClose} disabled={isDeleting}>
          {t('assistantsPage.modals.cancel')}
        </Button>
        <Button onClick={handleDelete} variant="contained" color="error" disabled={isDeleting}>
          {isDeleting ? t('assistantsPage.modals.deleting') : t('assistantsPage.modals.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
